import { expectDomTypeError } from "@milkdown/exception";
import { InputRule } from "@milkdown/prose/inputrules";
import { $inputRule, $nodeAttr, $nodeSchema, $remark } from "@milkdown/utils";
import DOMPurify from "dompurify";
import { get } from "node-emoji";
import remarkEmoji from "remark-emoji";
import twemoji from "twemoji";
import emojiRegex from "emoji-regex";
//#region src/__internal__/parse.ts
var setAttr = (text) => ({ title: text });
function parse(emoji, twemojiOptions) {
	return twemoji.parse(emoji, {
		attributes: setAttr,
		base: "https://cdn.jsdelivr.net/gh/twitter/twemoji/assets/",
		...twemojiOptions
	});
}
//#endregion
//#region src/__internal__/remark-twemoji.ts
var regex = emojiRegex();
var isParent = (node) => !!node.children;
var isLiteral = (node) => !!node.value;
function flatMap(ast, fn) {
	return transform(ast, 0, null)[0];
	function transform(node, index, parent) {
		if (isParent(node)) {
			const out = [];
			for (let i = 0, n = node.children.length; i < n; i++) {
				const nthChild = node.children[i];
				if (nthChild) {
					const xs = transform(nthChild, i, node);
					if (xs) for (let j = 0, m = xs.length; j < m; j++) {
						const item = xs[j];
						if (item) out.push(item);
					}
				}
			}
			node.children = out;
		}
		return fn(node, index, parent);
	}
}
var twemojiPlugin = (twemojiOptions) => {
	function transformer(tree) {
		flatMap(tree, (node) => {
			if (!isLiteral(node)) return [node];
			if (node.type === "code") return [node];
			const value = node.value;
			const output = [];
			let match;
			let str = value;
			while (match = regex.exec(str)) {
				const { index } = match;
				const emoji = match[0];
				if (emoji) {
					if (index > 0) output.push({
						...node,
						value: str.slice(0, index)
					});
					output.push({
						...node,
						value: parse(emoji, twemojiOptions),
						type: "emoji"
					});
					str = str.slice(index + emoji.length);
				}
				regex.lastIndex = 0;
			}
			if (str.length) output.push({
				...node,
				value: str
			});
			return output;
		});
	}
	return transformer;
};
//#endregion
//#region src/__internal__/with-meta.ts
function withMeta(plugin, meta) {
	Object.assign(plugin, { meta: {
		package: "@milkdown/plugin-emoji",
		...meta
	} });
	return plugin;
}
//#endregion
//#region src/index.ts
var emojiAttr = $nodeAttr("emoji", () => ({
	span: {},
	img: {}
}));
withMeta(emojiAttr, { displayName: "Attr<emoji>" });
var emojiSchema = $nodeSchema("emoji", (ctx) => ({
	group: "inline",
	inline: true,
	attrs: { html: { default: "" } },
	parseDOM: [{
		tag: "span[data-type=\"emoji\"]",
		getAttrs: (dom) => {
			if (!(dom instanceof HTMLElement)) throw expectDomTypeError(dom);
			return { html: DOMPurify.sanitize(dom.innerHTML) };
		}
	}],
	toDOM: (node) => {
		const attrs = ctx.get(emojiAttr.key)(node);
		const tmp = document.createElement("span");
		tmp.innerHTML = DOMPurify.sanitize(node.attrs.html);
		const firstChild = tmp.firstChild;
		if (!firstChild) {
			console.warn("Milkdown emoji plugin: emoji is not valid");
			return [
				"span",
				{
					...attrs.container,
					"data-type": "emoji"
				},
				tmp
			];
		}
		const dom = firstChild.cloneNode();
		tmp.remove();
		if (dom && dom instanceof HTMLElement) Object.entries(attrs.img).forEach(([key, value]) => dom.setAttribute(key, value));
		return [
			"span",
			{
				...attrs.container,
				"data-type": "emoji"
			},
			dom
		];
	},
	parseMarkdown: {
		match: ({ type }) => type === "emoji",
		runner: (state, node, type) => {
			state.addNode(type, { html: node.value });
		}
	},
	toMarkdown: {
		match: (node) => node.type.name === "emoji",
		runner: (state, node) => {
			const template = document.createElement("template");
			template.innerHTML = DOMPurify.sanitize(node.attrs.html);
			const img = template.content.querySelector("img");
			const title = img?.title || img?.alt;
			state.addNode("text", void 0, title);
		}
	}
}));
withMeta(emojiSchema.node, { displayName: "NodeSchema<emoji>" });
withMeta(emojiSchema.ctx, { displayName: "NodeSchemaCtx<emoji>" });
var remarkEmojiPlugin = $remark("remarkEmoji", () => remarkEmoji);
withMeta(remarkEmojiPlugin.plugin, { displayName: "Remark<remarkEmojiPlugin>" });
withMeta(remarkEmojiPlugin.options, { displayName: "RemarkConfig<remarkEmojiPlugin>" });
var remarkTwemojiPlugin = $remark("remarkTwemoji", () => twemojiPlugin);
withMeta(remarkTwemojiPlugin.plugin, { displayName: "Remark<remarkTwemojiPlugin>" });
withMeta(remarkTwemojiPlugin.options, { displayName: "RemarkConfig<remarkTwemojiPlugin>" });
var insertEmojiInputRule = $inputRule((ctx) => new InputRule(/(:([^:\s]+):)$/, (state, match, start, end) => {
	const content = match[0];
	if (!content) return null;
	const got = get(content);
	if (!got || content.includes(got)) return null;
	const html = parse(got, ctx.get(remarkTwemojiPlugin.options.key));
	return state.tr.setMeta("emoji", true).replaceRangeWith(start, end, emojiSchema.type(ctx).create({ html })).scrollIntoView();
}));
withMeta(insertEmojiInputRule, { displayName: "InputRule<insertEmojiInputRule>" });
var emoji = [
	emojiAttr,
	remarkEmojiPlugin,
	remarkTwemojiPlugin,
	emojiSchema,
	insertEmojiInputRule
].flat();
//#endregion
export { emoji, emojiAttr, emojiSchema, insertEmojiInputRule, remarkEmojiPlugin, remarkTwemojiPlugin };

//# sourceMappingURL=index.js.map